import React, { useEffect, useMemo, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import axios from 'axios';
import { HiOutlineDocumentPlus, HiOutlinePhoto } from 'react-icons/hi2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from './ui/PageHeader';

const EditBlog = () => {
  const { blog_id } = useParams();
  const navigate = useNavigate();
  const baseUrl = import.meta.env.VITE_LOCAL_BASE_URL;
  const apiUrl = 'https://api-img.eventplanet.in';
  const uploadEndpoint = 'api/auth/upload';
  const [value, setValue] = useState('');
  const [currentImage, setCurrentImage] = useState('');
  const [blogCat, setBlogCat] = useState([]);
  const [image, setImage] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState({
    keyword: '',
    meta_description: '',
    title: '',
    catSlug: '',
  });

  const errors = useMemo(
    () => ({
      title: !data.title.trim() ? 'Title is required.' : '',
      catSlug: !data.catSlug ? 'Category is required.' : '',
      keyword: !data.keyword.trim() ? 'Keyword is required.' : '',
      meta_description: !data.meta_description.trim() ? 'Meta description is required.' : '',
    }),
    [data],
  );

  const hasErrors = Boolean(errors.title || errors.catSlug || errors.keyword || errors.meta_description);

  function uploadAdapter(loader) {
    return {
      upload: () => {
        return new Promise((resolve, reject) => {
          const body = new FormData();
          loader.file.then((file) => {
            body.append('image', file);
            axios
              .post(`${apiUrl}/${uploadEndpoint}`, body, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              })
              .then((response) => {
                resolve({ default: response.data.url });
              })
              .catch((error) => {
                reject(error);
              });
          });
        });
      },
    };
  }

  function uploadPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
      return uploadAdapter(loader);
    };
  }

  useEffect(() => {
    const getSingleBlog = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/v1/blogs/single/${blog_id}`);
        const blog = response.data.data || {};
        setData({
          keyword: blog.keyword || '',
          meta_description: blog.meta_description || '',
          title: blog.title || '',
          catSlug: blog.cat_slug || blog.catSlug || '',
        });
        setValue(blog.description || '');
        setCurrentImage(blog.mobile_image || '');
      } catch (error) {
        toast.error('Blog details could not be loaded.');
      }
    };

    getSingleBlog();
  }, [baseUrl, blog_id]);

  useEffect(() => {
    const getBlogCat = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/v1/blog-categories`);
        setBlogCat(res.data.data || []);
      } catch (err) {
        toast.error('Categories could not be loaded.');
      }
    };

    getBlogCat();
  }, [baseUrl]);

  const btnHandler = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (hasErrors) {
      toast.error('Please complete the highlighted fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('meta_description', data.meta_description);
      formData.append('keyword', data.keyword);
      formData.append('cat_slug', data.catSlug);
      formData.append('description', value);

      if (image) {
        formData.append('image', image);
      }

      const res = await axios.put(`${baseUrl}/api/v1/blogs/${blog_id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(res.data.message);
      navigate('/dashboard/blog-list');
    } catch (err) {
      toast.error('Blog could not be updated. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formHandler = (event) => {
    const { name, value: fieldValue } = event.target;
    setData((currentData) => ({ ...currentData, [name]: fieldValue }));
  };

  const imgHandler = (event) => {
    const file = event.target.files[0];
    setImage(file || null);
  };

  return (
    <section className="page-stack">
      <PageHeader
        eyebrow="Blog Management"
        title="Edit Blog"
        description="Refine blog metadata, content, and category placement."
        actions={
          <Link to="/dashboard/blog-list" className="btn btn-soft-secondary">
            Back to Blogs
          </Link>
        }
      />

      <div className="surface-card form-card">
        <form method="post" onSubmit={btnHandler} noValidate>
          <div className="row g-4">
            <div className="col-12 col-lg-4">
              {currentImage && (
                <div className="current-image-preview">
                  <img src={currentImage} alt={data.title || 'Blog'} />
                </div>
              )}
              <label className="upload-dropzone" htmlFor="blog-image">
                <span className="upload-icon">
                  <HiOutlinePhoto aria-hidden="true" />
                </span>
                <strong>{image ? image.name : 'Replace cover image'}</strong>
                <small>Leave empty to keep the current cover.</small>
              </label>
              <input type="file" name="image" id="blog-image" className="visually-hidden" accept="image/*" onChange={imgHandler} />
            </div>

            <div className="col-12 col-lg-8">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label" htmlFor="blog-title">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="blog-title"
                    placeholder="Enter blog title"
                    className={`form-control form-control-lg ${submitted && errors.title ? 'is-invalid' : ''}`}
                    value={data.title}
                    onChange={formHandler}
                  />
                  {submitted && errors.title && <div className="invalid-feedback">{errors.title}</div>}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="blogcat">
                    Category
                  </label>
                  <select
                    className={`form-select form-control-lg ${submitted && errors.catSlug ? 'is-invalid' : ''}`}
                    id="blogcat"
                    onChange={formHandler}
                    name="catSlug"
                    value={data.catSlug}
                  >
                    <option value="">Select category</option>
                    {blogCat.map((cur) => (
                      <option key={cur.id || cur.slug} value={cur.slug}>
                        {cur.name}
                      </option>
                    ))}
                  </select>
                  {submitted && errors.catSlug && <div className="invalid-feedback">{errors.catSlug}</div>}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="blog-keyword">
                    Keyword
                  </label>
                  <input
                    type="text"
                    name="keyword"
                    id="blog-keyword"
                    placeholder="Primary keyword"
                    className={`form-control form-control-lg ${submitted && errors.keyword ? 'is-invalid' : ''}`}
                    value={data.keyword}
                    onChange={formHandler}
                  />
                  {submitted && errors.keyword && <div className="invalid-feedback">{errors.keyword}</div>}
                </div>

                <div className="col-12">
                  <label className="form-label" htmlFor="blog-meta-description">
                    Meta description
                  </label>
                  <textarea
                    name="meta_description"
                    id="blog-meta-description"
                    placeholder="Write a concise SEO description"
                    className={`form-control ${submitted && errors.meta_description ? 'is-invalid' : ''}`}
                    rows="3"
                    value={data.meta_description}
                    onChange={formHandler}
                  />
                  {submitted && errors.meta_description && <div className="invalid-feedback">{errors.meta_description}</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="editor-section">
            <label className="form-label">Content</label>
            <div className="editor-shell">
              <CKEditor
                config={{
                  extraPlugins: [uploadPlugin],
                  mediaEmbed: {
                    previewsInData: true,
                  },
                }}
                editor={ClassicEditor}
                data={value}
                onChange={(event, editor) => {
                  setValue(editor.getData());
                }}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                  Updating
                </>
              ) : (
                <>
                  <HiOutlineDocumentPlus aria-hidden="true" />
                  Update Blog
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default EditBlog;
